import { NextPage } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import { ParsedUrlQuery } from 'querystring';
import { useLoadAnalytics } from '@sy/app-client';

import BlogTemplate from '~/components/templates/BlogTemplate';
import { buildGetServerSideProps } from '~/getServerSideProps';
import { BlogPost as BlogPostPage } from '~/scenes/Blog';
import {
	getBlogPost,
	getBlogMainCategory,
	getRelatedPosts,
} from '~/services/blog';

import type { BlogCategoryUrl } from '~/services/blog/categories';
import type { CommonContext, InferProps } from '~/getServerSideProps';
import buildMetatadata from '~/utils/buildMetadata';

const Post: NextPage<InferProps<typeof getServerSideProps>> = ({
	activeCategory,
	pageUrl,
	post,
	relatedPosts,
	user,
}) => {
	useLoadAnalytics();

	return (
		<>
			<Head>
				<link rel="canonical" href={pageUrl} />
			</Head>
			{post.content?.includes('https://www.instagram.com') && (
				<Script id="instagram" src="https://www.instagram.com/embed.js" />
			)}
			<BlogTemplate activeCategory={activeCategory} user={user}>
				<BlogPostPage
					post={post}
					relatedPosts={relatedPosts}
					pageUrl={pageUrl}
				/>
			</BlogTemplate>
		</>
	);
};

interface UrlParams extends ParsedUrlQuery {
	slug?: string;
	categoryUrl?: BlogCategoryUrl;
}

const customServerSideProps = async (context: CommonContext<UrlParams>) => {
	const {
		params,
		query,
		locale,
		req: {
			headers: { host },
		},
	} = context;

	const { slug, categoryUrl } = params || {};
	const isPreview = !!query?.preview;

	// TODO support multiple locales, we force it to english for now until we have translated blog posts,
	// english is the only locale we have in blogs today so this give us feature parity
	// we support multi locale for preview only
	const post = await getBlogPost({
		locale: isPreview ? locale : 'en',
		categoryUrl,
		slug,
		isPreview,
	});

	if (!post) {
		return {
			redirect: {
				destination: '/blog',
				permanent: false,
			},
		};
	}

	const activeCategory = getBlogMainCategory(post.category);
	const relatedPosts = await getRelatedPosts({
		isPreview,
		locale: isPreview ? locale : 'en',
		relatedPostIds: post.relatedPosts,
	});

	const pageUrl = `https://${host}/blog/${post.urlPath}`;

	return {
		props: {
			pageUrl,
			post,
			relatedPosts,
			metadata: buildMetatadata({
				title: post.title,
				description: post.summary,
				twitterImgAlt: post.coverImage?.description,
				twitterImgUrl: post.coverImage?.url,
				ogImgAlt: post.coverImage?.description,
				ogImgUrl: post.coverImage?.url,
				ogImgHeight: post.coverImage?.dimensions?.height,
				ogImgWidth: post.coverImage?.dimensions?.width,
				ogUrl: pageUrl,
				ogType: 'article',
				ogDescription: post.summary,
				articleModifiedTime: post.updatedAt,
				...(post?.firstPublishedAt
					? { articlePublishedTime: post.firstPublishedAt }
					: {}),
				articlePublisher: 'https://www.facebook.com/streamyard',
			}),
			...(activeCategory ? { activeCategory } : {}),
		},
	};
};

export default Post;

export const getServerSideProps = buildGetServerSideProps({
	customServerSideProps,
});
